/**
 * ============================================================
 *  APEX DOORS — ملف المحتوى والوسائط المركزي
 *  CENTRAL CONTENT & MEDIA CONFIGURATION
 * ------------------------------------------------------------
 *  كل الصور، الفيديوهات، بيانات التواصل والتصنيفات تُعدّل من هنا.
 *  All media, contact details and categories are edited here.
 *
 *  لإضافة صورة أو فيديو جديد: أضف عنصرًا للمصفوفة المناسبة فقط.
 *  To add media: append an entry to the relevant array. No component
 *  changes are required.
 *
 *  media entry schema:
 *  { id, title_ar, title_en, type: 'image'|'video', src, poster,
 *    alt_ar, alt_en, category, finish_ar, finish_en,
 *    dimensions_ar?, dimensions_en?, externalUrl?, ratio? }
 * ============================================================
 */
import { testimonialPhotos } from "./designs";

export const brand = {
  name_en: "APEX DOORS",
  name_ar: "أبواب APEX",
  whatsappNumber: "972569689999",
  whatsappUrl: "https://wa.me/972569689999",
  telUrl: "tel:+972569689999",
  phoneDisplay: "+972-569689999",
  // ضع هنا روابط التواصل الاجتماعي الرسمية فقط. اتركها فارغة إن لم تتوفر.
  // Only add VERIFIED official social URLs. Leave empty if unavailable.
  social: [],
};

export const logo = {
  // اللوجو الرسمي المرفوع من العميل / official uploaded logo
  src: "https://customer-assets-eiarnc6j.emergentagent.net/job_1ba39efd-cbb6-4386-b0c1-85129d975f16/artifacts/lmpu67g7_%D9%84%D9%88%D8%AC%D9%88%D8%A7%D8%A8%D9%8A%D9%83%D8%B3.webp",
  alt_ar: "شعار أبواب APEX",
  alt_en: "APEX DOORS logo",
};

/* ---------- الصور الحقيقية لأعمال APEX / real APEX project photos ---------- */
const IMG_SCULPTED =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/5enidqww_WhatsApp%20Image%202026-10-05%20at%2004.35.02.jpeg";
const IMG_PORCELAIN =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/9n8z7oy4_WhatsApp%20Image%202026-10-05%20at%2004.35.04.jpeg";
const IMG_WOOD_FACADE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/hzrjqfh4_WhatsApp%20Image%202026-10-05%20at%2004.35.04%20%285%29.webp";
const IMG_WOOD_OPEN =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/dxfwhus3_WhatsApp%20Image%202026-10-05%20at%2004.35.05.webp";
const VID_PIVOT =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/sjg030dc_WhatsApp%20Video%202026-10-05%20at%2004.35.02.mp4";
const IMG_WORKSHOP =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/e0g1grxs_WhatsApp%20Image%202026-10-05%20at%2004.35.04%20%283%29.jpeg";
const IMG_GLASS_LINE =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/pv4agn4u_WhatsApp%20Image%202026-10-05%20at%2004.35.04%20%281%29.jpeg";
const IMG_INTERIOR =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/gorseixc_WhatsApp%20Image%202026-10-05%20at%2004.35.01.jpeg";
const IMG_LACE_PANEL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/mj6tl2ez_WhatsApp%20Image%202026-10-05%20at%2004.35.01%20%282%29.jpeg";
const IMG_MARBLE_PORTAL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_entrance-luxury-demo/artifacts/c3kmetjl_WhatsApp%20Image%202026-10-05%20at%2004.35.01%20%281%29.jpeg";

export const heroMedia = {
  // فيديو الخلفية في الواجهة الرئيسية (يُشغَّل مكتومًا تلقائيًا، مع زر صوت) / hero background video
  // الملف محلي في public/media: نسخة 720p مضغوطة (~4.8MB, faststart) من فيديو العميل "Apex Ad 1" + WebM احتياطي
  video: "/media/hero.mp4",
  videoWebm: "/media/hero.webm",
  // نسخة خفيفة للموبايل (480p) حتى يبدأ التشغيل التلقائي فورًا على شبكات الهاتف
  videoMobile: "/media/hero-mobile.mp4",
  image: IMG_WOOD_FACADE,
  ratio: 1500 / 2000,
  alt_ar: "مدخل فيلا بباب بيفوت ألمنيوم بمظهر خشبي مضلّع داخل واجهة حجرية",
  alt_en: "Villa entrance with a ribbed wood-look aluminum pivot door in a stone facade",
};

export const galleryCategories = [
  { id: "all", label_ar: "الكل", label_en: "All" },
  { id: "panels", label_ar: "ألواح ديكورية", label_en: "Decorative panels" },
  { id: "porcelain", label_ar: "بورسلان", label_en: "Porcelain" },
  { id: "wood", label_ar: "تشطيبات بلمسة خشبية", label_en: "Wood-look finishes" },
];

/* فئات التصاميم داخل مجسّم المدخل (تشمل الحجر والـ CNC من التصاميم المفاهيمية) */
export const visualizerCategories = [
  ...galleryCategories.filter((c) => c.id !== "all"),
  { id: "stone", label_ar: "حجر", label_en: "Stone" },
  { id: "cnc", label_ar: "CNC", label_en: "CNC" },
];

const apexGallery = [
  {
    id: "apex-sculpted-facet",
    title_ar: "تصميم منحوت",
    title_en: "Sculpted Facet",
    type: "image",
    src: IMG_SCULPTED,
    poster: IMG_SCULPTED,
    ratio: 720 / 1600,
    category: "panels",
    finish_ar: "ألواح ديكورية رمادية مع تفاصيل ثلاثية الأبعاد",
    finish_en: "Grey decorative panels with three-dimensional detailing",
    alt_ar: "باب بيفوت بتصميم منحوت بألواح رمادية وتفاصيل هندسية بارزة",
    alt_en: "Pivot door with sculpted grey panels and raised geometric detailing",
  },
  {
    id: "apex-porcelain-line",
    title_ar: "بورسلان بخط أسود",
    title_en: "Porcelain Line",
    type: "image",
    src: IMG_PORCELAIN,
    poster: IMG_PORCELAIN,
    ratio: 1200 / 1600,
    category: "porcelain",
    finish_ar: "بورسلان رمادي مع شريط ألمنيوم أسود ومقبض طولي",
    finish_en: "Grey porcelain with a black aluminum band and full-height handle",
    alt_ar: "باب بيفوت مفتوح بتلبيس بورسلان رمادي وشريط أسود ومقبض طولي",
    alt_en: "Open pivot door clad in grey porcelain with a black band and long handle",
  },
  {
    id: "apex-ribbed-timber",
    title_ar: "مظهر خشبي مضلّع",
    title_en: "Ribbed Timber Look",
    type: "image",
    src: IMG_WOOD_FACADE,
    poster: IMG_WOOD_FACADE,
    ratio: 1500 / 2000,
    category: "wood",
    finish_ar: "مظهر خشبي بتضليع عمودي داخل واجهة حجرية",
    finish_en: "Wood-look finish with vertical ribbing within a stone facade",
    alt_ar: "مدخل بباب بيفوت بمظهر خشبي مضلّع ضمن واجهة حجرية",
    alt_en: "Entrance with a ribbed wood-look pivot door set in a stone facade",
  },
  {
    id: "apex-pivot-open",
    title_ar: "مظهر خشبي — حركة المحور",
    title_en: "Timber Look — Pivot Motion",
    type: "image",
    src: IMG_WOOD_OPEN,
    poster: IMG_WOOD_OPEN,
    ratio: 1500 / 2000,
    category: "wood",
    finish_ar: "مظهر خشبي مضلّع مع مقابض ألمنيوم ومحور أرضي",
    finish_en: "Ribbed wood-look with aluminum handles and a floor pivot",
    alt_ar: "باب بيفوت بمظهر خشبي مفتوح ويظهر المحور الأرضي",
    alt_en: "Open wood-look pivot door revealing the floor pivot mechanism",
  },
  {
    id: "apex-marble-portal",
    title_ar: "بوابة رخامية",
    title_en: "Marble Portal",
    type: "image",
    src: IMG_MARBLE_PORTAL,
    poster: IMG_MARBLE_PORTAL,
    ratio: 610 / 1356,
    category: "porcelain",
    finish_ar: "بورسلان بنقشة رخامية داكنة مع تلبيس مطابق للمدخل",
    finish_en: "Dark marble-pattern porcelain with matching portal cladding",
    alt_ar: "باب بيفوت بتلبيس بورسلان رخامي داكن داخل مدخل بنفس التلبيس",
    alt_en: "Pivot door in dark marble-pattern porcelain within a matching clad portal",
  },
  {
    id: "apex-glass-line",
    title_ar: "خط زجاجي",
    title_en: "Glass Line",
    type: "image",
    src: IMG_GLASS_LINE,
    poster: IMG_GLASS_LINE,
    ratio: 1200 / 1600,
    category: "porcelain",
    finish_ar: "بورسلان رمادي مع شريط زجاجي طولي وقفل ذكي",
    finish_en: "Grey porcelain with a full-height glass strip and smart lock",
    alt_ar: "باب بيفوت بتلبيس بورسلان رمادي وشريط زجاجي عمودي وقفل ذكي",
    alt_en: "Pivot door in grey porcelain with a vertical glass strip and smart lock",
  },
  {
    id: "apex-lace-panel",
    title_ar: "لوح مفرّغ",
    title_en: "Laser-cut Panel",
    type: "image",
    src: IMG_LACE_PANEL,
    poster: IMG_LACE_PANEL,
    ratio: 720 / 1600,
    category: "panels",
    finish_ar: "لوح ديكوري مفرّغ بنقشة عربية على كامل الارتفاع",
    finish_en: "Laser-cut decorative panel with an arabesque pattern, full height",
    alt_ar: "باب بيفوت عالي بلوح ديكوري مفرّغ بنقشة عربية",
    alt_en: "Tall pivot door with a laser-cut arabesque decorative panel",
  },
  {
    id: "apex-interior-warm",
    title_ar: "مدخل داخلي بإضاءة دافئة",
    title_en: "Warm Interior Entry",
    type: "image",
    src: IMG_INTERIOR,
    poster: IMG_INTERIOR,
    ratio: 1200 / 1600,
    category: "wood",
    finish_ar: "مظهر خشبي دافئ مع تفاصيل ألمنيوم ومقبض مدمج",
    finish_en: "Warm wood-look finish with aluminum detailing and an integrated handle",
    alt_ar: "باب بمظهر خشبي مفتوح على شرفة مع خزانة بإضاءة دافئة",
    alt_en: "Wood-look door opening onto a terrace beside a warmly lit cabinet",
  },
];

/* المعرض يعرض أعمال APEX الحقيقية فقط؛ التصاميم المفاهيمية تظهر داخل مجسّم المدخل */
export const gallery = apexGallery;

/**
 * آراء العملاء — نصوص توضيحية مع صور مشاريع توضيحية.
 * استبدلها بآراء وصور حقيقية عند توفّرها (الاسم، المدينة، النص، الصورة، التقييم).
 */
export const testimonials = [
  {
    id: "t1",
    name_ar: "أبو كريم", name_en: "Abu Karim",
    city_ar: "رام الله", city_en: "Ramallah",
    rating: 5,
    text_ar: "والله الباب طلع أحلى من الصورة. ركّبوه بنص نهار وما خلّوا ورا هم ولا وسخة. كل اللي بيفوت عالبيت بيسأل منين جبته. يعطيكم العافية يا شباب.",
    text_en: "Honestly the door came out better than the photo. They installed it in half a day and left the place spotless. Everyone who walks in asks where I got it. Well done guys.",
    photo: testimonialPhotos.ramallah,
  },
  {
    id: "t2",
    name_ar: "رنا وأحمد", name_en: "Rana & Ahmad",
    city_ar: "نابلس", city_en: "Nablus",
    rating: 5,
    text_ar: "كنا خايفين من الباب الكبير إنه يكون تقيل عالفتح، بس بينفتح بإصبع. الخشبي المضلّع عطى البيت شكل تاني. شكرًا كتير على الصبر علينا بالاختيار.",
    text_en: "We were worried such a big door would be heavy to open, but it moves with one finger. The ribbed wood look gave the house a whole new feel. Thanks for your patience while we chose.",
    photo: testimonialPhotos.nablus,
  },
  {
    id: "t3",
    name_ar: "د. ليث", name_en: "Dr. Laith",
    city_ar: "البيرة", city_en: "Al-Bireh",
    rating: 5,
    text_ar: "اخترنا اللوح المفرّغ بالنحاسي، وصراحة لما بيضوي بالليل منظر. البصمة بتشتغل من أول مرة، الولاد بطّلوا يضيّعوا مفاتيح. تعامل راقي من أول رسالة واتساب.",
    text_en: "We went with the laser-cut panel in brass and honestly when it lights up at night it's a view. The fingerprint works first try, the kids stopped losing keys. Classy service from the first WhatsApp message.",
    photo: testimonialPhotos.albireh,
  },
  {
    id: "t4",
    name_ar: "م. سامر — مقاول", name_en: "Eng. Samer — Contractor",
    city_ar: "جنين", city_en: "Jenin",
    rating: 5,
    text_ar: "ركّبنا معهم باب مدخل لعمارة سكنية. القياس كان مظبوط عالمليم والتسليم بوقته. السكان مبسوطين والباب شغّال من سنة بدون ولا مشكلة. إن شاء الله نكمّل بالمشاريع الجاية.",
    text_en: "We did an apartment building entrance with them. Measurements were spot on and delivery was on time. Residents are happy and the door has run a year with zero issues. We'll continue on upcoming projects.",
    photo: testimonialPhotos.jenin,
  },
  {
    id: "t5",
    name_ar: "هبة م.", name_en: "Hiba M.",
    city_ar: "بيت لحم", city_en: "Bethlehem",
    rating: 5,
    text_ar: "بيتنا حجر قديم وكنت خايفة الباب الحديث ما يلبق. طلع الترافرتين عالحجر تحفة، كأنه من البيت نفسه. شرحولي كل تفصيلة قبل ما يصنّعوا وما صار ولا مفاجأة.",
    text_en: "Our house is old stone and I feared a modern door wouldn't suit it. The travertine on the stone turned out gorgeous, like it was always part of the house. They explained every detail before making it — no surprises.",
    photo: testimonialPhotos.bethlehem,
  },
  {
    id: "t6",
    name_ar: "جورج ح.", name_en: "George H.",
    city_ar: "الناصرة", city_en: "Nazareth",
    rating: 5,
    text_ar: "طلعتنا ضيّقة ومنحدرة وكنت مفكّر صعب يركّبوا باب بهالحجم. إجوا، قاسوا، وبعد أسبوعين الباب بمكانه. البلوط مع الإطار الأسود لبّق على الحجر كتير. تسلم إيديكم.",
    text_en: "Our street is narrow and steep and I thought a door this size would be impossible. They came, measured, and two weeks later it was in place. The oak with the black frame suits the stone perfectly. Great work.",
    photo: testimonialPhotos.nazareth,
  },
  {
    id: "t7",
    name_ar: "أبو محمد", name_en: "Abu Mohammad",
    city_ar: "كفر مندا", city_en: "Kafr Manda",
    rating: 5,
    text_ar: "بنيت الفيلا وبدي باب يلفت النظر من الشارع. الأبيض مع شريط الزجاج الأسود عمل الفرق. الشباب وصلوا من الضفة ورجعوا بنفس اليوم وكل شي كان جاهز. الله يوفقكم.",
    text_en: "I built the villa and wanted a door that stands out from the street. The white with the black glass strip made the difference. The team came and finished the same day, everything was ready. God bless.",
    photo: testimonialPhotos.kafrmanda,
  },
  {
    id: "t8",
    name_ar: "سهى ع.", name_en: "Suha A.",
    city_ar: "الطيرة", city_en: "Tira",
    rating: 5,
    text_ar: "حبّيت الأسود مع شقوق الخشب من أول ما شفته عالموقع، وجرّبته على صورة بيتنا بالمجسّم قبل ما نقرّر. طلع بالضبط زي ما شفناه. شكرًا على الدقة والذوق.",
    text_en: "I loved the black with wood slots the moment I saw it on the site, and tried it on a photo of our house in the visualizer before deciding. It turned out exactly as we saw it. Thanks for the precision and taste.",
    photo: testimonialPhotos.tira,
  },
];

export const videos = [
  {
    id: "apex-pivot-reel",
    title_ar: "حركة باب بيفوت بتلبيس بورسلان",
    title_en: "Porcelain pivot door in motion",
    type: "video",
    src: VID_PIVOT,
    poster: IMG_MARBLE_PORTAL,
    ratio: 9 / 16,
    orientation: "portrait",
    alt_ar: "فيديو يظهر حركة باب بيفوت بتلبيس بورسلان أثناء الفتح",
    alt_en: "Video showing a porcelain-clad pivot door opening",
    externalUrl: null,
  },
];

/**
 * المواد والتشطيبات. إن لم تتوفر صورة مطابقة اتركي image = null
 * وسيظهر نموذج تلبيس معرّف بوضوح بدل محاولة تمثيل باب غير حقيقي.
 */
export const materials = [
  {
    id: "porcelain",
    name_ar: "البورسلان",
    name_en: "Porcelain",
    desc_ar: "نقشات وتفاصيل تمنح المدخل حضورًا أنيقًا.",
    desc_en: "Patterns and detail that give the entrance an elegant presence.",
    image: IMG_PORCELAIN,
    focus: "28% 55%",
    real: true,
  },
  {
    id: "stone",
    name_ar: "الحجر",
    name_en: "Stone",
    desc_ar: "تلبيس ينسجم مع طابع الواجهة.",
    desc_en: "Cladding that harmonises with the character of the facade.",
    image: null,
    real: false,
  },
  {
    id: "wood",
    name_ar: "المظهر الخشبي",
    name_en: "Wood-look",
    desc_ar: "دفء بصري يناسب التصاميم الحديثة والكلاسيكية.",
    desc_en: "Visual warmth suited to both modern and classic designs.",
    image: IMG_WOOD_FACADE,
    focus: "38% 45%",
    real: true,
  },
  {
    id: "panels",
    name_ar: "الألواح الديكورية",
    name_en: "Decorative panels",
    desc_ar: "ألوان ونقشات متنوعة لخيارات تصميم مختلفة.",
    desc_en: "A range of colours and patterns for different design directions.",
    image: IMG_SCULPTED,
    focus: "50% 40%",
    real: true,
  },
];

export const motionMedia = {
  image: IMG_WOOD_OPEN,
  alt_ar: "تفصيل المحور الأرضي لباب بيفوت أثناء الفتح",
  alt_en: "Detail of the floor pivot of an opening door",
};

export const aboutMedia = {
  image: IMG_WORKSHOP,
  alt_ar: "لوح ديكوري مفرّغ بنقشة عربية على طاولة العمل في ورشة APEX",
  alt_en: "Laser-cut arabesque decorative panel on the worktable in the APEX workshop",
};

/* ---------------- WhatsApp helpers ---------------- */
export const waLink = (message) =>
  `${brand.whatsappUrl}?text=${encodeURIComponent(message)}`;
